import React, { Component, Fragment } from "react";
import {
  Row,
  Col,
  Input,
  Popconfirm,
  Icon,
  Spin,
  Select,
  Tooltip,
  Button,
  Alert,
  Skeleton,
} from "antd";
import { fetchDriverPayInfo, saveDriverPayInfo, deleteDriverPayInfo } from "../../api/Billing";
import { fetchDriverList } from "../../api/UsersApi";
import userStore from "../../stores/UserStore";
import { alertMessage, isEmpty } from "../../common/Common";
import I18n from "../../common/I18n";
import DeleteIcon from "../../components/common/DeleteIcon";
import _ from "lodash";
import NoteMessage from "../NoteMessage";

const ROW_HEIGHT = 46; // approximate px height per row
const MAX_VISIBLE_ROWS = 5;
const SKELETON_ROW_COUNT = 3; 


const SkeletonRow = () => (
  <Row gutter={32} style={{ marginTop: 10 }}>
    <Col xs={24} style={{ marginBottom: 6 }}>
      {/* Driver select placeholder */}
      <Col xs={8}>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 1, width: "80%" }}
          style={{ lineHeight: "32px" }}
        />
      </Col>
      {/* Amount input placeholder */}
      <Col xs={8}>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 1, width: "80%" }}
          style={{ lineHeight: "32px" }}
        />
      </Col>
      {/* Action icons placeholder */}
      <Col xs={8}>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 1, width: "40%" }}
          style={{ lineHeight: "32px" }}
        />
      </Col>
    </Col>
  </Row>
);

const SkeletonLoader = () => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #dcdcdc",
      borderRadius: "4px",
      padding: "16px",
      marginBottom: "16px",
    }}
  >
    {/* Section title */}
    <Row>
      <Col xs={24} className="alignLeft">
        <Skeleton
          active
          title={{ width: 180 }}
          paragraph={false}
          style={{ marginBottom: 8 }}
        />
      </Col>
    </Row>

    {/* Note bar */}
    <Skeleton
      active
      title={false}
      paragraph={{ rows: 1, width: "70%" }}
      style={{ marginBottom: 12 }}
    />

    {/* Column headers */}
    <Row gutter={32}>
      <Col xs={24}>
        <Col xs={8}>
          <Skeleton active title={{ width: 80 }} paragraph={false} />
        </Col>
        <Col xs={8}>
          <Skeleton active title={{ width: 120 }} paragraph={false} />
        </Col>
        <Col xs={8} />
      </Col>
    </Row>

    {/* Data rows */}
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
      <SkeletonRow key={`skeleton_row_${i}`} />
    ))}

    {/* Action buttons */}
    <Row className="marginTop20">
      <Col xs={24} className="alignCenter">
        <Skeleton active title={false} paragraph={{ rows: 1, width: 220 }} />
      </Col>
    </Row>
  </div>
);

class OrderPickupDriverPay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isInitialLoad: true,
      inProgress: false,
      drivers: [],
      allDriverPayments: [],
      orderDriverPayments: [
        {
          order_id: this.props.orderResponse?.id,
          order_no: this.props.orderResponse?.customer_order_details?.customer_order_number,
          driver_payments: [],
        },
      ],
      decimalPoints: window.localStorage.getItem("round_off_decimals") || 2,
    };
  }

  componentDidMount() {
    this.getDrivers();
    this.getdriverPayInfo();
  }

  getDrivers = () => {
    const orgId = userStore.getStateValue("selectedOrg");
    fetchDriverList({ unscoped: true, orgId, includeWH: false}).then((result) => {
      if (result.success) {
        const drivers = result.data?.drivers || [];
        this.setState({ drivers });
      }
    });
  };

  getdriverPayInfo = () => {
    const orderId = this.props.orderResponse?.id;
    if (!orderId) return;

    this.setState((prev) =>
      prev.isInitialLoad ? {} : { inProgress: true }
    );

    fetchDriverPayInfo([orderId]).then((result) => {
      if (result.success) {
        const order_driver_payments = result.order_driver_payments || [];
        this.setDriverPayments(order_driver_payments);
      } else {
        alertMessage(result.errors || "Error fetching driver pay info", "error", 10);
        this.setState({ inProgress: false, isInitialLoad: false });
      }
    });
  };

  setDriverPayments = (order_driver_payments) => {
    const orderId = this.props.orderResponse?.id;
    const orderNo =
      this.props.orderResponse?.customer_order_details?.customer_order_number;
    let newOrderDriverPayments = [
      {
        order_id: orderId,
        order_no: orderNo,
        driver_payments: [],
      },
    ];

    let allDriverPayments = [];

    if (order_driver_payments && order_driver_payments.length > 0) {
      const rec = order_driver_payments.find(
        (r) => r.customer_order_id === orderId
      );
      if (rec && rec.driver_payments) {
        allDriverPayments = rec.driver_payments;

        const flatPayments = allDriverPayments.filter(
          (dp) => dp.amount_type === "FLAT_PAY"
        );
        newOrderDriverPayments[0].driver_payments = flatPayments.map((dp) => ({
          ...dp,
          amount_type: "FLAT_PAY",
        }));
      }
    }

    if (newOrderDriverPayments[0].driver_payments.length === 0) {
      newOrderDriverPayments[0].driver_payments = [
        {
          driver_id: "",
          amount: "",
          amount_type: "FLAT_PAY",
          delivered_driver: false,
        },
      ];
    }

    this.setState({
      orderDriverPayments: newOrderDriverPayments,
      allDriverPayments,
      inProgress: false,
      isInitialLoad: false,
    });
  };

  handleDetailsChange = (index, element, value) => {
    const orderDriverPayments = [...this.state.orderDriverPayments];
    const targetRow = orderDriverPayments[0].driver_payments[index];
    if (targetRow) {
      targetRow[element] = value;
      this.setState({ orderDriverPayments });
    }
  };

  addDriverPayRow = () => {
    const orderDriverPayments = [...this.state.orderDriverPayments];
    orderDriverPayments[0].driver_payments.push({
      driver_id: "",
      amount: "",
      amount_type: "FLAT_PAY",
      delivered_driver: false,
    });
    this.setState({ orderDriverPayments });
  };

  removeDriverPayRow = (rowIndex) => {
    const orderDriverPayments = [...this.state.orderDriverPayments];
    if (
      orderDriverPayments.length > 0 &&
      orderDriverPayments[0].driver_payments.length > 0
    ) {
      orderDriverPayments[0].driver_payments.splice(rowIndex, 1);
      this.setState({ orderDriverPayments });
    }
  };

  buildDriverPayDeletePayload = (customerOrders = [], screenFrom = "ORDER") => ({
    screen_from: screenFrom,
    customer_orders: customerOrders.map((order) => ({
      order_id: order.order_id,
      order_number: order.order_no || order.order_number,
      driver_payments: (order.driver_payments || [])
        .filter((dp) => !isEmpty(dp.driver_id) && !isEmpty(dp.amount))
        .map((dp) => ({
          driver_id: dp.driver_id,
          amount: dp.amount,
          amount_type: dp.amount_type || "FLAT_PAY",
          delivered_driver: !!dp.delivered_driver,
        })),
    })),
  });

  handleDeleteDriverPayRow = (rowIndex) => {
    const currentOrder = this.state.orderDriverPayments[0];
    const deletedRow = currentOrder?.driver_payments?.[rowIndex];

    if (
      !currentOrder ||
      !deletedRow ||
      isEmpty(deletedRow.driver_id) ||
      isEmpty(deletedRow.amount)
    ) {
      this.removeDriverPayRow(rowIndex);
      return;
    }

    const isPersistedRow = (this.state.allDriverPayments || []).some((row) => {
      return (
        String(row.driver_id) === String(deletedRow.driver_id) &&
        String(row.amount_type || "FLAT_PAY") === String(deletedRow.amount_type || "FLAT_PAY") &&
        Number(row.amount) === Number(deletedRow.amount)
      );
    });

    if (!isPersistedRow) {
      this.removeDriverPayRow(rowIndex);
      return;
    }

    const customer_order_ids = [currentOrder.order_id];
    const payload = this.buildDriverPayDeletePayload([
      {
        order_id: currentOrder.order_id,
        order_no: currentOrder.order_no,
        driver_payments: [deletedRow],
      },
    ], "ORDER");

    this.setState({ inProgress: true });
    deleteDriverPayInfo(customer_order_ids, payload).then((result) => {
      if (result.success) {
        this.removeDriverPayRow(rowIndex);
        this.setState({ inProgress: false });
      } else {
        alertMessage(
          result.errors || "Failed to delete driver pay row",
          "error",
          10
        );
        this.setState({ inProgress: false });
      }
    });
  };

  handleClear = () => {
    const { orderDriverPayments } = this.state;
    const orderId = orderDriverPayments[0]?.order_id;
    if (!orderId) return;

    const payload = this.buildDriverPayDeletePayload(orderDriverPayments, "ORDER");
    this.setState({ inProgress: true });
    deleteDriverPayInfo([orderId], payload).then((result) => {
      if (result.success) {
        alertMessage(result.message || "Cleared Successfully", "success", 10);
        this.getdriverPayInfo();
      } else {
        alertMessage(result.errors || "Failed to clear", "error", 10);
        this.setState({ inProgress: false });
      }
    });
  };

  handleSave = () => {
    const orderDriverPayments = [...this.state.orderDriverPayments];
    const currentOrder = orderDriverPayments[0];

    const invalidRow = currentOrder.driver_payments.findIndex(
      (record) =>
        (!isEmpty(record.driver_id) && isEmpty(record.amount)) ||
        (isEmpty(record.driver_id) && !isEmpty(record.amount))
    );

    if (invalidRow >= 0) {
      alertMessage("Please Select Driver and enter Amount", "error");
      return;
    }

    this.setState({ inProgress: true });

    const validPayments = currentOrder.driver_payments
      .filter((rec) => !isEmpty(rec.driver_id) && !isEmpty(rec.amount))
      .map((dp) => ({
        ...dp,
        amount_type: "FLAT_PAY",
        delivered_driver: false,
      }));

    const customerOrders = [];
    if (validPayments.length > 0) {
      customerOrders.push({
        order_id: currentOrder.order_id,
        order_number: currentOrder.order_no,
        driver_payments: validPayments,
      });
    }

    const accountId =
      this.props.account?.id ||
      this.props.account?.account_id ||
      this.props.orderResponse?.account?.account_id ||
      this.props.orderResponse?.customer_order_details?.account_id;

    const data = {
      customer_orders: customerOrders,
      customer_order_ids: [currentOrder.order_id],
      account_id: accountId,
      organization_id: userStore.getStateValue("selectedOrg"),
      screen_from : "ORDER"
    };

    saveDriverPayInfo(data).then((result) => {
      if (result.success) {
        alertMessage("Saved Successfully");
        this.getdriverPayInfo();
      } else {
        alertMessage(result.errors || "Failed to save", "error", 10);
        this.setState({ inProgress: false });
      }
    });
  };

  render() {
    const {
      orderDriverPayments,
      drivers,
      inProgress,
      decimalPoints,
      isInitialLoad,
      allDriverPayments,
    } = this.state;
    const { isLocked } = this.props;
    const isOrderLocked =
      this.props.orderResponse?.customer_order_details?.is_locked === "true" ||
      this.props.orderResponse?.customer_order_details?.is_locked === true;

      const isOrderDeleted =
      this.props.orderResponse?.customer_order_details?.is_deleted_order === "true" ||
      this.props.orderResponse?.customer_order_details?.is_deleted_order === true;
    const isDisabled = isLocked || isOrderLocked || isOrderDeleted;
    if (isInitialLoad) {
      return <SkeletonLoader />;
    }

    const currentOrder = orderDriverPayments[0];
    if (!currentOrder) return null;

    const driverPayments = currentOrder.driver_payments;
    const hasNoRows = driverPayments.length === 0;
    const needsScroll = driverPayments.length > MAX_VISIBLE_ROWS;
    const scrollHeight = MAX_VISIBLE_ROWS * ROW_HEIGHT;

    const getAvailableDrivers = (currentRowIndex) => {
      // Drivers selected in other FLAT_PAY rows
      const selectedDriverIdsInOtherRows = driverPayments
        .filter((dp, index) => index !== currentRowIndex && !isEmpty(dp.driver_id))
        .map((dp) => dp.driver_id);

      // Drivers assigned to pickup / delivery locations
      const locationDriverIds = [];

      const pickupDriverId = this.props.orderResponse?.pickup_location_details?.driver_details?.id;
      const deliveryDriverId = this.props.orderResponse?.delivery_location_details?.driver_details?.id;

      if (pickupDriverId) locationDriverIds.push(pickupDriverId);
      if (deliveryDriverId) locationDriverIds.push(deliveryDriverId);

      const nonFlatPayDriverIds = allDriverPayments
        .filter((dp) => dp.amount_type !== "FLAT_PAY")
        .map((dp) => dp.driver_id);

      const allExcludedDriverIds = [
        ...selectedDriverIdsInOtherRows,
        ...locationDriverIds,
        ...nonFlatPayDriverIds,
      ];

      // Only active (non-deleted) drivers in the list
      const available = drivers.filter(
        (driver) => !driver.deleted_at && !allExcludedDriverIds.includes(driver.id)
      );

      // If this row already has a deleted driver selected, inject them back
      // so the Select can display their info instead of showing blank/id
      const currentRowDriverId = driverPayments[currentRowIndex]?.driver_id;
      if (currentRowDriverId) {
        const alreadyIncluded = available.some((d) => d.id === currentRowDriverId);
        if (!alreadyIncluded) {
          const deletedDriver = drivers.find((d) => d.id === currentRowDriverId);
          if (deletedDriver) {
            return [deletedDriver, ...available];
          }
        }
      }

      return available;
    };

    return (
      <Spin spinning={inProgress}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #dcdcdc",
            borderRadius: "4px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <Row>
            <Col xs={24} className="alignLeft">
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#000000",
                  fontFamily: "Lato",
                  lineHeight: "24px",
                }}
              >
                Pickup Driver Pays:
              </span>
            </Col>
          </Row>

          <div className="config-header-row">
            <p className="note-message">
              <sup style={{ color: "red", fontSize: 22, top: 3 }}>*</sup>{" "}
              <strong>Note:</strong>{" "}
              {
                "Payments added here process as Fixed/Flat Pay. For percentage-based Eligible Pay, use the Billing screen."
              }
            </p>
          </div>

          {hasNoRows ? (
            <Fragment>
              <Alert
                message={
                  <h4 style={{ margin: 0 }}>
                    No Pickup Driver Payment Configuration is done for above order.
                    {!isDisabled && (
                      <Button type="link" onClick={() => this.addDriverPayRow()}>
                        Click here to configure
                      </Button>
                    )}
                  </h4>
                }
                type="info"
                showIcon
                style={{ paddingBottom: 0, paddingTop: 2 }}
              />
            </Fragment>
          ) : (
            <div style={{ marginTop: 10 }}>
              {/* Fixed header row */}
              <Row gutter={32}>
                <Col xs={24}>
                  <Col xs={8}>
                    <span style={{ fontWeight: 700 }}>
                      <sup style={{ color: "red" }}>*</sup>
                      {I18n.t("general.driver")}:
                    </span>
                  </Col>
                  <Col xs={8}>
                    <span style={{ fontWeight: 700 }}>
                      <sup style={{ color: "red" }}>*</sup>
                      Flat Pay Amount:
                    </span>
                  </Col>
                  <Col xs={8} />
                </Col>
              </Row>

              {/* Scrollable body */}
              <div
                style={
                  needsScroll
                    ? {
                        maxHeight: scrollHeight,
                        overflowY: "auto",
                        marginTop: 6,
                        paddingRight: 4,
                      }
                    : { marginTop: 6 }
                }
              >
                {driverPayments.map((driverInfoRow, index) => (
                  <Row
                    className="marginTop10"
                    key={`orderRow_pickup_${index}`}
                    gutter={32}
                  >
                    <Col xs={24} style={{ marginBottom: 6 }}>
                      <Col xs={8}>
                        <Select
                          showSearch
                          value={driverInfoRow.driver_id || ""}
                          onChange={(e) =>
                            this.handleDetailsChange(index, "driver_id", e)
                          }
                          filterOption={(input, option) => {
                            const children = option.props.children;
                            if (children && children.length > 0) {
                              if (Array.isArray(children)) {
                                return (
                                  children[0]
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                );
                              } else {
                                return (
                                  children
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                );
                              }
                            }
                          }}
                          style={{ width: "80%", height: "20%" }}
                          disabled={isDisabled}
                        >
                          {getAvailableDrivers(index).map((driver) => (
                            <Select.Option
                              key={driver.id} 
                              value={driver.id}
                            >
                              {driver.employee_code}
                              {driver.deleted_at ? (
                                <span style={{ color: "#ff4d4f", fontSize: 11, marginLeft: 6 }}>
                                  (Deleted)
                                </span>
                              ) : null}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={8}>
                        <Input
                          type="number"
                          value={driverInfoRow.amount}
                          onChange={(e) =>
                            this.handleDetailsChange(
                              index,
                              "amount",
                              e.target.value
                            )
                          }
                          min={0}
                          precision={decimalPoints}
                          style={{ width: "80%", height: "20%" }}
                          disabled={isDisabled}
                        />
                      </Col>
                      <Col xs={8}>
                        <Row gutter={16} type="flex">
                          <Col>
                            {!isDisabled && (
                              <Popconfirm
                                placement="topRight"
                                title={I18n.t("messages.confirm")}
                                onConfirm={() => 
                                  this.handleDeleteDriverPayRow(index)
                                }
                                okText="Yes"
                                cancelText="No"
                              >
                                <Tooltip title={I18n.t("general.delete")}>
                                  <DeleteIcon className="textBold fontSize18 cursorPointer" />
                                  &nbsp;&nbsp;
                                </Tooltip>
                              </Popconfirm>
                            )}
                            {!isDisabled &&
                              driverPayments.length - 1 === index &&
                              !isEmpty(driverInfoRow.driver_id) &&
                              !isEmpty(driverInfoRow.amount) && (
                                <Icon
                                  type="plus-circle"
                                  onClick={() => this.addDriverPayRow()}
                                  className="marginLeft10 fontSize18 cursorPointer"
                                  style={{ color: "#1B263C" }}
                                />
                              )}
                          </Col>
                        </Row>
                      </Col>
                    </Col>
                  </Row>
                ))}
              </div>
            </div>
          )}

          {/* Save / Clear buttons — hidden when no rows */}
          {!isDisabled && !hasNoRows && (
            <Row className="marginTop20">
              <Col xs={24} className="alignCenter">
                <Button
                  type="default"
                  className="clearButtonStyle"
                  onClick={this.handleClear}
                  icon="close-circle"
                >
                  {I18n.t("general.clear")}
                </Button>
                <span style={{ display: "inline-block", width: 16 }} />
                <Button
                  type="primary"
                  onClick={this.handleSave}
                  style={{ minWidth: 100 }}
                  icon="save"
                >
                  {I18n.t("general.save")}
                </Button>
              </Col>
            </Row>
          )}
        </div>
      </Spin>
    );
  }
}

export default OrderPickupDriverPay;